import { useTranslation } from 'react-i18next'

import { QA_NAME_CONFIRMATION } from '../../../../../constants/qa'
import { useMassActions, useNotification } from '../../../../../hooks'
import { ConfirmationModal } from '../../../../Confirmation'
import {
  APPLICANT_ACTION_ARCHIVE,
  APPLICANT_ACTION_ARCHIVE_MASS,
} from '../../RentalPortalApplicants.constants'
import { performMockApplicantsMassAction } from '../../RentalPortalApplicants.mocks'
import { getApplicantsMassActions } from '../../RentalPortalApplicants.utils'

import {
  getApplicantsMassActionDescription,
  getApplicantsMassActionEligibleItems,
  getApplicantsMassActionFailures,
  getApplicantsMassActionFailureText,
  getApplicantsMassActionItems,
  getApplicantsMassActionRequest,
  getNotificationTitle,
} from './useRentalPortalApplicantsMassActions.utils'

import ArchiveIcon from '../../../../../icons/delete.svg'

export function useRentalPortalApplicantsMassActions({
  allItems,
  excludeIds,
  filterValues,
  selectAllPages,
  selected,
  setModal,
  onCleanSelection,
  onClose,
  onReload,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification, handleAddWarningNotification } =
    useNotification()

  const { handleAddFinishMassActionNotification } = useMassActions({
    getNotificationTitle,
  })

  const massActionItems = getApplicantsMassActionItems({
    allItems,
    excludeIds,
    selectAllPages,
    selected,
  })

  const eligibleItems = getApplicantsMassActionEligibleItems(massActionItems)

  const massActions = getApplicantsMassActions({
    eligibleCount: eligibleItems.length,
    selectedSize: massActionItems.length,
    t,
  })

  function addResultNotification({ action, resultItems, successIds, onRetry }) {
    const failures = getApplicantsMassActionFailures({
      items: allItems,
      resultItems,
      t,
    })

    if (failures.count === 0) {
      handleAddFinishMassActionNotification(
        { items: resultItems, success_ids: successIds },
        { action, totalCount: resultItems.length }
      )

      return
    }

    const notification = {
      text: getApplicantsMassActionFailureText({ failures, t }),
      title:
        successIds.length > 0
          ? getNotificationTitle({
              action,
              count: successIds.length,
              t,
              totalCount: resultItems.length,
            })
          : t('RentalPortal.ApplicantMassActionErrorKey'),
    }

    if (successIds.length > 0) {
      handleAddWarningNotification(notification)

      return
    }

    handleAddErrorNotification({
      ...notification,
      textButtonValue: t('RentalPortal.ListingRetryBtnKey'),
      onClickTextButton: onRetry,
    })
  }

  function massAction({ action, ids, onRetry }) {
    return performMockApplicantsMassAction(
      getApplicantsMassActionRequest({
        action,
        eligibleIds: eligibleItems.map(applicant => applicant.id),
        excludeIds,
        filterValues,
        ids,
        selectAllPages,
      })
    )
      .then(({ data }) => {
        onClose()

        addResultNotification({
          action,
          resultItems: data.items,
          successIds: data.success_ids,
          onRetry,
        })

        if (data.success_ids.length === 0) {
          return null
        }

        onCleanSelection()

        return onReload()
      })
      .catch(() => {
        onClose()

        handleAddErrorNotification({
          textButtonValue: t('RentalPortal.ListingRetryBtnKey'),
          title: t('RentalPortal.ApplicantMassActionErrorKey'),
          onClickTextButton: onRetry,
        })
      })
  }

  function showArchiveConfirmModal({ action, description, ids, onRetry }) {
    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitProps: { beforeIconProps: { Icon: ArchiveIcon } },
          submitText: t('RentalPortal.ApplicantProfileArchiveBtnKey'),
        }}
        description={description}
        qaName={QA_NAME_CONFIRMATION}
        onCancel={onClose}
        onSubmit={() => massAction({ action, ids, onRetry })}
      />
    )
  }

  function setMassAction(action) {
    if (eligibleItems.length === 0) {
      return null
    }

    switch (action) {
      case APPLICANT_ACTION_ARCHIVE_MASS:
        showArchiveConfirmModal({
          action,
          description: getApplicantsMassActionDescription({
            eligibleItems,
            selectedCount: massActionItems.length,
            t,
          }),
          onRetry: () => setMassAction(action),
        })
        break
      default:
        return null
    }
  }

  function handleArchiveApplicant(applicant) {
    showArchiveConfirmModal({
      action: APPLICANT_ACTION_ARCHIVE,
      description: getApplicantsMassActionDescription({
        eligibleItems: [applicant],
        selectedCount: 1,
        t,
      }),
      ids: [applicant.id],
      onRetry: () => handleArchiveApplicant(applicant),
    })
  }

  return { handleArchiveApplicant, massAction, massActions, setMassAction }
}
